import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import CharacterDetail from "./pages/CharacterDetail";
import Chat from "./pages/Chat";
import Chats from "./pages/Chats";
import CreateCharacter from "./pages/CreateCharacter";
import Profile from "./pages/Profile";
import Discover from "./pages/Discover";
import Settings from "./pages/Settings";
import AccountSettings from "./pages/AccountSettings";
import SocialSettings from "./pages/SocialSettings";
import AboutSettings from "./pages/AboutSettings";
import Collection from "./pages/Collection";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import AuthCallback from "./pages/AuthCallback";
import CreatePersona from "./pages/CreatePersona";
import CreatorRewards from "./pages/CreatorRewards";
import PublicProfile from "./pages/PublicProfile";
import Community from "./pages/Community";
import Notifications from "./pages/Notifications";
import GenerateAvatar from "./pages/GenerateAvatar";
import GemShop from "./pages/GemShop";
import GemSuccess from "./pages/GemSuccess";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Moderation from "./pages/Moderation";
import Onboarding from "./pages/Onboarding";
import Memory from "./pages/Memory";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import Welcome from "./pages/Welcome";
import AdminDashboard from "./pages/AdminDashboard";
import Search from "./pages/Search";
import Leaderboard from "./pages/Leaderboard";
import CreatorCenter from "./pages/CreatorCenter";
import Membership from "./pages/Membership";
import { useAuth } from "./contexts/AuthContext";

// Show landing page to logged-out visitors at "/", real Home to logged-in users
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return user ? <Home /> : <Welcome />;
}

function AppRouter() {
  const location = window.location;
  // Check URL fragment for session_id from Google OAuth
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/moderation" element={
        <ProtectedRoute>
          <Moderation />
        </ProtectedRoute>
      } />
      <Route path="/character/:id" element={<CharacterDetail />} />
      <Route path="/discover" element={<Discover />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/memory" element={
        <ProtectedRoute>
          <Memory />
        </ProtectedRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/chat/:id" element={
        <ProtectedRoute>
          <Chat />
        </ProtectedRoute>
      } />
      <Route path="/chats" element={
        <ProtectedRoute>
          <Chats />
        </ProtectedRoute>
      } />
      <Route path="/create" element={
        <ProtectedRoute>
          <CreateCharacter />
        </ProtectedRoute>
      } />
      <Route path="/character/:editId/edit" element={
        <ProtectedRoute>
          <CreateCharacter />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={<Profile />} />
      <Route path="/membership" element={
        <ProtectedRoute>
          <Membership />
        </ProtectedRoute>
      } />
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/user/:userId" element={<PublicProfile />} />
      <Route path="/community" element={<Search />} />
      <Route path="/news" element={<Search />} />
      <Route path="/search" element={<Search />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/creator-center" element={
        <ProtectedRoute>
          <CreatorCenter />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Notifications />
        </ProtectedRoute>
      } />
      <Route path="/generate-avatar" element={
        <ProtectedRoute>
          <GenerateAvatar />
        </ProtectedRoute>
      } />
      <Route path="/rewards" element={
        <ProtectedRoute>
          <CreatorRewards />
        </ProtectedRoute>
      } />
      <Route path="/collection" element={
        <ProtectedRoute>
          <Collection />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccess />
        </ProtectedRoute>
      } />
      <Route path="/gems" element={
        <ProtectedRoute>
          <GemShop />
        </ProtectedRoute>
      } />
      <Route path="/gems/success" element={
        <ProtectedRoute>
          <GemSuccess />
        </ProtectedRoute>
      } />
      
      {/* Settings Routes */}
      <Route path="/persona/create" element={
        <ProtectedRoute>
          <CreatePersona />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/settings/account" element={
        <ProtectedRoute>
          <AccountSettings />
        </ProtectedRoute>
      } />
      <Route path="/settings/socials" element={
        <ProtectedRoute>
          <SocialSettings />
        </ProtectedRoute>
      } />
      <Route path="/settings/about" element={
        <ProtectedRoute>
          <AboutSettings />
        </ProtectedRoute>
      } />
      
      {/* Placeholder routes for settings subpages */}
      <Route path="/settings/language" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/settings/parental" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Layout>
            <AppRouter />
          </Layout>
          <Toaster position="top-center" />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
